import { isRecord } from '@willbooster/shared-lib';
import {
  createMemoizeCacheGroup,
  getCacheKeyOfStringified,
  getGlobalMemoizeCacheStore,
  type MemoizeCache,
  type MemoizeCacheRegistry,
  memoizeFactory,
} from 'at-decorators';

import { displayTimeZone } from '@/utils/timeZone';

export const memoize = memoizeFactory({ getCacheKey: getCacheKeyOfStringified, maxCacheSizePerTarget: 10_000 });

const MEMOIZE_CACHE_STORE_KEY = Symbol.for('__exercodeMemoizeCacheStore');

const memoizeCacheNames = [
  'chatAvailability',
  'courseAdminLevel',
  'homeStatistics',
  'freshMaterials',
  'getCourseMonitorPageData',
  'helpDocs',
  'headers',
  'staticContents',
  'users',
] as const;

type MemoizeCacheName = (typeof memoizeCacheNames)[number];
type MemoizeCacheStore = Record<MemoizeCacheName, MemoizeCacheRegistry>;

const memoizeCacheStore = getMemoizeCacheStore();

function getMemoizeCacheStore(): MemoizeCacheStore {
  const store = (globalThis as typeof globalThis & Record<symbol, unknown>)[MEMOIZE_CACHE_STORE_KEY];
  if (isMemoizeCacheStore(store)) {
    return store;
  }

  return getGlobalMemoizeCacheStore(MEMOIZE_CACHE_STORE_KEY, memoizeCacheNames);
}

function isMemoizeCacheStore(value: unknown): value is MemoizeCacheStore {
  return isRecord(value) && memoizeCacheNames.every((name) => isMemoizeCacheRegistry(value[name]));
}

function isMemoizeCacheRegistry(value: unknown): value is MemoizeCacheRegistry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const registry = value as Partial<MemoizeCacheRegistry>;
  return (
    typeof registry.length === 'number' &&
    typeof registry.push === 'function' &&
    typeof registry[Symbol.iterator] === 'function'
  );
}

export const COURSE_MONITOR_PAGE_DATA_CACHE_DURATION_MS = process.env.WB_ENV === 'production' ? 1 * 60 * 1000 : 0;
export const COURSE_MONITOR_PAGE_DATA_MAX_CACHE_SIZE_PER_TARGET = 100;
const getCourseMonitorPageDataCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: COURSE_MONITOR_PAGE_DATA_MAX_CACHE_SIZE_PER_TARGET,
  cacheDuration: COURSE_MONITOR_PAGE_DATA_CACHE_DURATION_MS,
  caches: memoizeCacheStore.getCourseMonitorPageData,
});
export const clearCachesForGetCourseMonitorPageData = getCourseMonitorPageDataCache.clear;
export function memoizeForGetCourseMonitorPageData<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  const memoized = getCourseMonitorPageDataCache.memoize(func);
  return (...args) => {
    deleteExpiredMemoizeCacheEntries(
      getCourseMonitorPageDataCache.caches,
      Date.now(),
      COURSE_MONITOR_PAGE_DATA_CACHE_DURATION_MS
    );
    return memoized(...args);
  };
}

export function deleteExpiredMemoizeCacheEntries(
  caches: Iterable<MemoizeCache>,
  now: number,
  cacheDurationMs: number
): void {
  for (const cache of caches) {
    for (const [key, [, cachedAt]] of cache) {
      if (now - cachedAt > cacheDurationMs) cache.delete(key);
    }
  }
}

const headersCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 1000,
  cacheDuration: 10 * 60 * 1000,
  caches: memoizeCacheStore.headers,
});
export const clearCachesForHeaders = headersCache.clear;
export function memoizeForHeaders<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  return headersCache.memoize(func);
}

const freshMaterialsCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 100,
  cacheDuration: 1 * 60 * 1000,
  caches: memoizeCacheStore.freshMaterials,
});
export const clearCachesForFreshMaterials = freshMaterialsCache.clear;
export function memoizeForFreshMaterials<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  return freshMaterialsCache.memoize(func);
}

const staticContentsCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 1000,
  cacheDuration: 15 * 60 * 1000,
  caches: memoizeCacheStore.staticContents,
});
export const clearCachesForStaticContents = staticContentsCache.clear;
export function memoizeForStaticContents<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  const memoized = staticContentsCache.memoize(func);
  return (...args) => {
    const result = memoized(...args);
    if (result instanceof Promise) {
      void result.catch(() => {
        // Remove only this failed attempt: invalidation may already have started a fresh lookup.
        for (const cache of staticContentsCache.caches) {
          for (const [key, [value]] of cache) {
            if (value === result) cache.delete(key);
          }
        }
      });
    }
    return result;
  };
}

const helpDocsCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 1000,
  caches: memoizeCacheStore.helpDocs,
});
export const clearCachesForHelpDocs = helpDocsCache.clear;
export function memoizeForHelpDocs<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  return helpDocsCache.memoize(func);
}

const usersCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 10_000,
  cacheDuration: 24 * 60 * 60 * 1000,
  caches: memoizeCacheStore.users,
});
export const clearCachesForUsers = usersCache.clear;
export function memoizeForUsers<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  return usersCache.memoize(func);
}

const chatAvailabilityCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 10_000,
  cacheDuration: 60 * 1000,
  caches: memoizeCacheStore.chatAvailability,
});
export const clearCachesForChatAvailability = chatAvailabilityCache.clear;
export function memoizeForChatAvailability<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  return chatAvailabilityCache.memoize(func);
}

// ホームの総提出数はマーケティング用の概数（万単位で丸めて表示）のため、長めにキャッシュする。
const homeStatisticsCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 10,
  cacheDuration: 60 * 60 * 1000,
  caches: memoizeCacheStore.homeStatistics,
});
export const clearCachesForHomeStatistics = homeStatisticsCache.clear;
export function memoizeForHomeStatistics<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  return homeStatisticsCache.memoize(func);
}

const courseAdminLevelCache = createMemoizeCacheGroup({
  getCacheKey: getCacheKeyOfStringified,
  maxCacheSizePerTarget: 10_000,
  cacheDuration: 30 * 60 * 1000,
  caches: memoizeCacheStore.courseAdminLevel,
});
export const clearCachesForCourseAdminLevel = courseAdminLevelCache.clear;
export function memoizeForCourseAdminLevel<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  return courseAdminLevelCache.memoize(func);
}

export const createDateTimeFormatter = memoize(
  (locale?: string, options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
    return new Intl.DateTimeFormat(locale, { timeZone: displayTimeZone, ...options });
  }
);
